import React, { ReactChild, useCallback, useEffect, useState, useMemo } from 'react';
import {
  ActivityIndicator,
  Clipboard,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { ScreenProps } from '../../../typesInterfacesEnums/componentProps';
import { ComponentWrapper } from '../../helperComponents/ComponentWrapper';
import { Divider } from '../../helperComponents/Divider';
import { applyTransparency } from '../../../constants/colors';
import { mbApplyTransparency, mbPlatformStyle } from '@mightybyte/rnw.utils.style-utils';
import { BackButton } from '../../helperComponents/BackButton';
import { useOpenClose } from '../../../utils/hooks';
import { EditPopup } from '../../helperComponents/EditPopup';
import { RemovePopup } from '../../helperComponents/RemovePopup';
import { HiveUserType, useHiveUsersQuery } from './useHiveUsersQuery.gql';
import { useRemoveUserFromHiveMutation } from './useRemoveUserFromHiveMutation';
import { useEditUserFromHiveMutation } from './useEditUserFromHiveMutation.gql';
import { ApolloError } from '@apollo/client';
import { ErrorPopup } from '../../helperComponents/ErrorPopup';
import { isMobile, isMobileApp } from '@mightybyte/rnw.utils.device-info';
import { MB_accessTokenUtils } from '@mightybyte/rnw.utils.access-token-utils';
import { utils } from '../../../utils/utils';
import { envs } from '../../../../env';
import { MenuButton } from '../../helperComponents/MenuButton';
import { WEB_POST_MESSAGE_OBJECT_TYPES } from '../ServerRedirect/serverRedirectUtils';
import {
  MB_EVENT_EMMITER_EVENT_TYPE,
  MB_EventEmmiter,
  MB_EventObject,
} from '@mightybyte/rnw.utils.event-emmiter';
import { HiveSize, PaymentStatus, sizeTitle } from '../../../constants/constants';
import { useGetHiveByIdQuery } from './useGetHiveByIdQuery.gql';
import { PaymentPopup } from '../../helperComponents/PaymentPopup';
import { RefreshButton } from '../../helperComponents/RefreshButton';
import { ChangeNameIcon } from '../../../resources/svgComponents/ChangeNameIcon';
import { ChangeHiveNamePopup } from '../../helperComponents/ChangeHiveNamePopup';
import { useChangeHiveNameMutation } from './useChangeHiveNameMutation';
import { useForceActivateHiveMutation } from './useForceActivateHiveMutation.gql';
import {
  LeaderBoardButton,
  createLeaderboardData,
  LeaderBoard,
} from '../../helperComponents/LeaderBoard';
import { getErrorText } from '../../../utils/errors';
import { SeatIcon } from '../../../resources/svgComponents/SeatIcon';
import { SeatXIcon } from '../../../resources/svgComponents/SeatXIcon';
import { ClockIcon } from '../../../resources/svgComponents/ClockIcon';
import { ControllerIcon } from '../../../resources/svgComponents/ControllerIcon';
import { useGetHiveAnalyticsQuery } from './useGetHiveAnalyticsQuery.gql';
import { UserItem } from './UserItem/UserItem';
import { OnBoardPopup } from '../../helperComponents/OnBoardPopup';
import { BackButtonV2 } from '../../helperComponents/BackButtonV2';
import { handleError } from '@apollo/client/link/http/parseAndCheckHttpResponse';
import { EditIcon } from '../../../resources/svgComponents/EditIcon';
import { CopyIcon } from '../../../resources/svgComponents/CopyIcon';
import { IconTextButton } from '../../helperComponents/Button/IconTextButton';
import { RefreshIcon } from '../../../resources/svgComponents/RefreshIcon';
import HexagonIconButton from '../../helperComponents/Button/HexagonIconButton';
import ChevronRight from 'react-native-vector-icons/FontAwesome';
import ChevronDown from 'react-native-vector-icons/FontAwesome';
import { HexagonFile } from '../../../resources/svgComponents/HexagonFile';
import { FileIcon } from '../../../resources/svgComponents/FileIcon';
import { SettingsIcon } from '../../../resources/svgComponents/SettingsIcon';
import { UserItemSmall } from './UserItem/UserItemSmall';
import { MB_Button } from '@mightybyte/rnw.components.button';
import CalendarIcon from '../../../resources/svgComponents/CalendarIcon';
import { DateRange, DateRangeModal } from '../../helperComponents/DateRangeModal';
import { visible } from 'chalk';
import { HexagonSettings } from '../../../resources/svgComponents/HexagonSettings';

const toUTCDate = (date?: string) => {
  if (!date) return undefined;
  const d = new Date(date + 'T00:00:00'); // local midnight
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString().split('T')[0];
};

const InfoCard = ({ icon, title, value }: { icon: ReactChild; title: string; value: string }) => {
  return (
    <View style={styles.infoCard}>
      <View style={styles.infoCardHeader}>
        <Text style={styles.infoKey}>{title}</Text>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <View
          style={{
            marginRight: 11,
            backgroundColor: '#FCEABB',
            borderRadius: 8,
            width: 42,
            height: 42,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {icon}
        </View>
        <Text style={styles.infoValue} numberOfLines={1}>
          {value}
        </Text>
      </View>
    </View>
  );
};

const MyHiveData = ({ navigation, route }: ScreenProps<'MyHiveData'>) => {
  const { id } = route.params;

  const { width } = useWindowDimensions();
  const tableLimitPx = width < 1340;
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>({});
  const [isCalendarVisible, setCalendarVisible] = useState(false);

  // TODO: We might need to paginate this in the future.
  const {
    error: getHiveUsersError,
    data: { hiveUsers: users } = {},
    refetch: refetchHiveUsers,
    loading: hiveUsersLoading,
  } = useHiveUsersQuery({
    hiveId: id,
    page: 1,
    pageSize: 2000,
    startDate: toUTCDate(dateRange.startDate),
    endDate: toUTCDate(dateRange.endDate),
  });
  const {
    error: getHiveDataError,
    data: { hive } = {},
    refetch: refetchHiveData,
    loading: hiveDataLoading,
  } = useGetHiveByIdQuery(id);
  const {
    error: getHiveAnalyticsDataError,
    data: { getHiveAnalytics: hiveAnalytics } = {},
    refetch: refetchHiveAnalyticsData,
    loading: hiveAnalyticsDataLoading,
  } = useGetHiveAnalyticsQuery(id, toUTCDate(dateRange.startDate), toUTCDate(dateRange.endDate));

  const [removeUser, { loading: removeUserFromHiveLoading }] = useRemoveUserFromHiveMutation();
  const [editUser, { loading: editUserFromHiveLoading }] = useEditUserFromHiveMutation();
  const [changeHiveName, { loading: changeHiveNameLoading }] = useChangeHiveNameMutation();

  const [selectedUser, setSelectedUser] = useState<HiveUserType | undefined>(undefined);

  const [isChangeNamePopupVisible, setChangeNamePopupOpen, setChangeNamePopupClose] =
    useOpenClose();
  const [isPaymentPopupVisible, setPaymentPopupOpen, setPaymentPopupClose] = useOpenClose();
  const [isRemovePopupVisible, setRemovePopupOpen, setRemovePopupClose] = useOpenClose();
  const [isEditPopupVisible, setEditPopupOpen, setEditPopupClose] = useOpenClose();

  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [isOnBoardVisible, setIsOnBoardVisible] = useState(false);
  const [hiveCopied, setHiveCopied] = useState(false);

  const handleCopyHiveCode = useCallback(() => {
    Clipboard.setString(hive?.code ?? '');
    setHiveCopied(true);
    setTimeout(() => setHiveCopied(false), 2000);
  }, [hive?.code]);

  const [hasError, setHasError] = useState({
    visible: false,
    popup: false,
    text: '',
  });

  const handlePay = async () => {
    if (!isMobileApp) {
      let token = await MB_accessTokenUtils.getAccessToken();
      token = token?.replace('Bearer ', '');
      utils.openExternalWindow(
        envs.API_URL + `/api/subscription/stripe/checkout?hiveId=${id}&accessToken=${token}`,
        'Checkout',
      );
    }
  };

  const [forceActivateHiveMutation] = useForceActivateHiveMutation();
  const [isActivated, setIsActivated] = useState(false);
  const handleForceActivate = useCallback(async () => {
    try {
      await forceActivateHiveMutation({ variables: { hiveId: id } });
      setIsActivated(true);
      refetchHiveData();
    } catch (error) {
      console.log(error);
      setHasError({ popup: true, visible: true, text: 'Failed to activate hive' });
    }
  }, [forceActivateHiveMutation, id, refetchHiveData]);

  const stripeRedirectCallback = useCallback(
    (event: MB_EventObject) => {
      try {
        if (
          event.origin !== envs.WEBSITE_BASE_URL ||
          (event.data.type !== WEB_POST_MESSAGE_OBJECT_TYPES.STRIPE_PAYMENT &&
            event.data.type !== WEB_POST_MESSAGE_OBJECT_TYPES.GENERAL_ERROR)
        ) {
          return;
        }
        if (event.data.success && event.data.type !== WEB_POST_MESSAGE_OBJECT_TYPES.GENERAL_ERROR) {
          refetchHiveData();
        } else {
          return;
        }
      } catch (error) {
        console.log(error);
        setHasError({ popup: true, visible: true, text: 'Something went wrong' });
      }
    },
    [refetchHiveData],
  );

  const handleEditPress = (user: any) => {
    setSelectedUser(user);
    setEditPopupOpen();
  };
  const handleRemovePress = (user: any) => {
    setSelectedUser(user);
    setRemovePopupOpen();
  };

  const hidePopups = useCallback(() => {
    setRemovePopupClose();
    setEditPopupClose();
    setChangeNamePopupClose();
  }, [setChangeNamePopupClose, setEditPopupClose, setRemovePopupClose]);

  const handleError = useCallback(
    (err: ApolloError) => {
      hidePopups();
      setHasError({ popup: true, visible: true, text: getErrorText(err) });
    },
    [hidePopups],
  );

  const onActionDone = useCallback(() => {
    // hide modals
    hidePopups();
    // reset selected user
    setSelectedUser(undefined);
    // get the new data
    refetchHiveUsers();
    refetchHiveData();
  }, [hidePopups, refetchHiveData, refetchHiveUsers]);

  const handleRemoveSubmitPress = () => {
    if (selectedUser) {
      removeUser({ variables: { userId: selectedUser.id, hiveId: id } })
        .then(onActionDone)
        .catch(handleError);
    }
  };

  const handleEditSubmitPress = (
    firstName: string,
    lastName: string,
    userName: string,
    password: string,
  ) => {
    if (selectedUser) {
      const variables: any = {
        userId: selectedUser.id,
        firstName,
        lastName,
        userName,
        ...(password ? { password } : {}), // only include password if it has value
      };

      editUser({ variables }).then(onActionDone).catch(handleError);
    }
  };
  const handleChangeName = (name: string) => {
    if (hive) {
      changeHiveName({ variables: { hiveId: hive.id, name: name } })
        .then(onActionDone)
        .catch(handleError);
    }
  };

  const getStatusText = () => {
    if (hive?.paymentStatus === PaymentStatus.Active) {
      return <Text style={{ fontWeight: '700', color: '#409853' }}>Active</Text>;
    }
    if (isMobileApp) {
      return <Text style={{ fontWeight: '700', color: '#E02E2E' }}>Inactive</Text>;
    }
    if (hive?.paymentStatus === PaymentStatus.Pending) {
      return <Text style={{ fontWeight: '700', color: '#E89823' }}>Pending</Text>;
    }
    return <Text style={{ fontWeight: '700', color: '#E02E2E' }}>Not paid</Text>;
  };

  useEffect(() => {
    const eventSub = MB_EventEmmiter.addListener(
      MB_EVENT_EMMITER_EVENT_TYPE.message,
      stripeRedirectCallback,
    );

    return () => {
      eventSub.remove();
    };
  }, [stripeRedirectCallback]);

  useEffect(() => {
    if (getHiveUsersError) {
      handleError(getHiveUsersError);
    } else if (getHiveDataError) {
      handleError(getHiveDataError);
    } else if (getHiveAnalyticsDataError) {
      handleError(getHiveAnalyticsDataError);
    }
  }, [getHiveUsersError, getHiveDataError, getHiveAnalyticsDataError, handleError]);

  useEffect(() => {
    if (hive && hive.paymentStatus !== PaymentStatus.Active && !isMobile && !envs.BYPASS_PAYMENT) {
      setPaymentPopupOpen();
    }
  }, [hive, setPaymentPopupOpen]);

  const leaderBoardData = useMemo(() => {
    return createLeaderboardData({
      users: users ?? [],
    });
  }, [users]);

  // for loading
  if (!hive || hiveUsersLoading || hiveDataLoading || hiveAnalyticsDataLoading) {
    return (
      <ComponentWrapper hideBee hasInset>
        <View style={{ justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <ActivityIndicator size={'large'} color={'E89823'} />
        </View>
      </ComponentWrapper>
    );
  }

  return (
    <>
      <ComponentWrapper
        hideBee
        hasInset
        scrollEnabled
        hasScroll={true}
        style={{ paddingHorizontal: 0 }}
      >
        {/* <LeaderBoard
          data={leaderBoardData}
          isVisible={showLeaderboard}
          onClose={() => {
            setShowLeaderboard(false);
          }}
        /> */}
        <View style={styles.topContainer}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <BackButtonV2
              onPress={() =>
                navigation.reset({ index: 1, routes: [{ name: 'Lobby' }, { name: 'MyHives' }] })
              }
            />
            {!tableLimitPx && (
              <View
                style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' }}
              >
                <Text style={styles.title}>{hive.name}</Text>
                <TouchableOpacity onPress={setChangeNamePopupOpen} style={[styles.editName]}>
                  <EditIcon />
                </TouchableOpacity>
                <View style={{ position: 'relative' }}>
                  <View style={[styles.chips, styles.hiveKeyChip]}>
                    <Text style={[styles.chipsText]}>
                      Hive Key: <Text style={styles.bold}>{hive.code}</Text>
                    </Text>
                    <TouchableOpacity onPress={handleCopyHiveCode} style={styles.copyButton}>
                      {hiveCopied
                        ? <Text style={styles.copiedIcon}>✓</Text>
                        : <CopyIcon size={18} />}
                    </TouchableOpacity>
                  </View>
                  {hiveCopied && <Text style={styles.copiedLabel}>Copied!</Text>}
                </View>
                <View style={styles.chips}>
                  <Text style={styles.chipsText}>
                    Size: <Text style={styles.bold}>{sizeTitle[hive.size]}</Text>
                  </Text>
                </View>
              </View>
            )}
          </View>

          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {!tableLimitPx && (
              <View style={{ marginRight: 12 }}>
                <MB_Button
                  title={
                    dateRange.startDate && dateRange.endDate
                      ? `${dateRange.startDate} – ${dateRange.endDate}`
                      : 'Select a Date Range'
                  }
                  style={styles.date}
                  leftElement={<CalendarIcon fill="#000000" />}
                  textStyle={{ color: '#9F9F9F' }}
                  rightElement={
                    <ChevronDown name="chevron-down" size={14} style={{ color: '#9F9F9F' }} />
                  }
                  onPress={e => {
                    e.preventDefault?.();
                    setCalendarVisible(true);
                  }}
                />
              </View>
            )}
            {tableLimitPx && (
              <>
                <View style={[{ marginRight: 10 }]}>
                  <IconTextButton
                    icon={
                      <SettingsIcon
                        width={30}
                        height={30}
                        color={tableLimitPx ? '#E08500' : 'black'}
                      />
                    }
                    onPress={() => {
                      navigation.navigate('HiveSettings', { ...route.params });
                    }}
                    containerStyle={{ backgroundColor: '#FFCF53', width: 49 }}
                  />
                </View>
                <View style={[{ marginRight: 10 }]}>
                  <IconTextButton
                    icon={
                      <FileIcon width={28} height={20} color={tableLimitPx ? '#E08500' : 'black'} />
                    }
                    onPress={() => {
                      navigation.navigate('HiveReports', { ...route.params });
                    }}
                    containerStyle={{ backgroundColor: '#FFCF53', width: 49 }}
                  />
                </View>
              </>
            )}

            <IconTextButton
              icon={
                <RefreshIcon width={28} height={20} color={tableLimitPx ? '#E08500' : 'black'} />
              }
              onPress={() => {
                refetchHiveData();
                refetchHiveUsers();
                refetchHiveAnalyticsData();
              }}
              containerStyle={{ backgroundColor: '#FFCF53', width: 49 }}
            />

            {/* {!isMobile && (
                <Text style={styles.onBoardText} onPress={() => setIsOnBoardVisible(true)}>
                  How to onboard users?
                </Text>
              )} */}
          </View>
        </View>

        {tableLimitPx && (
          <>
            <View style={[styles.smallUserInfoContainer]}>
              <View style={[{ flexDirection: 'row', alignItems: 'center' }]}>
                <Text style={styles.title}>{hive.name}</Text>
                <TouchableOpacity
                  onPress={setChangeNamePopupOpen}
                  style={[styles.editName, { backgroundColor: 'transparent' }]}
                >
                  <EditIcon color={'#E87030'} />
                </TouchableOpacity>
              </View>
              <View style={[{ flexDirection: 'row', alignItems: 'center', marginTop: 20 }]}>
                <View style={{ position: 'relative' }}>
                  <View style={[styles.chips, styles.hiveKeyChip, { backgroundColor: '#FFF5DD' }]}>
                    <Text style={[styles.chipsText, { color: '#000000' }]}>
                      Hive Key: <Text style={styles.bold}>{hive.code}</Text>
                    </Text>
                    <TouchableOpacity onPress={handleCopyHiveCode} style={styles.copyButton}>
                      {hiveCopied
                        ? <Text style={styles.copiedIcon}>✓</Text>
                        : <CopyIcon size={18} />}
                    </TouchableOpacity>
                  </View>
                  {hiveCopied && <Text style={styles.copiedLabel}>Copied!</Text>}
                </View>
                <View style={[styles.chips, { backgroundColor: '#FFF5DD' }]}>
                  <Text style={[styles.chipsText, { color: '#000000' }]}>
                    Size: <Text style={styles.bold}>{sizeTitle[hive.size]}</Text>
                  </Text>
                </View>
              </View>
            </View>
            <View
              style={{
                marginTop: 15,
                width: '100%',
                alignItems: 'center',
                paddingHorizontal: isMobile ? 20 : 60,
              }}
            >
              <MB_Button
                title={
                  dateRange.startDate && dateRange.endDate
                    ? `${dateRange.startDate} – ${dateRange.endDate}`
                    : 'Select a Date Range'
                }
                style={[styles.date, { width: '100%', maxWidth: 385 }]}
                leftElement={<CalendarIcon fill="#000000" />}
                textStyle={{ color: '#9F9F9F' }}
                rightElement={
                  <ChevronDown name="chevron-down" size={14} style={{ color: '#9F9F9F' }} />
                }
                onPress={e => {
                  e.preventDefault?.();
                  setCalendarVisible(true);
                }}
              />
            </View>
          </>
        )}

        <Divider empty height={20} />
        <View style={{ justifyContent: 'center', alignItems: 'center' }}>
          <Divider empty height={16} />
          {/* <View>
            <Text style={styles.statusText}>Status: {getStatusText()}</Text>
          </View> */}
          {!isMobileApp && !isActivated && hive.paymentStatus !== PaymentStatus.Active ? (
            <>
              <Divider empty height={24} />
              {envs.BYPASS_PAYMENT ? (
                <MenuButton
                  onPress={handleForceActivate}
                  colors={['#AAAAAA', '#888888']}
                  text="Hive is locked pending payment. Click to assume payment received"
                  textStyle={{ fontSize: 14, fontWeight: '400' }}
                  height={50}
                  width={340}
                />
              ) : (
                <MenuButton
                  onPress={() => handlePay()}
                  colors={['#90DF75', '#62B655']}
                  text="Pay now"
                  textStyle={{ fontSize: 16, fontWeight: '400' }}
                  height={50}
                  width={160}
                />
              )}
            </>
          ) : null}
        </View>
        <View style={[styles.infoContainer, tableLimitPx && { justifyContent: 'center' }]}>
          <View style={[!tableLimitPx && { flexDirection: 'row', marginTop: 20 }]}>
            <View style={[tableLimitPx && { marginBottom: 10 }]}>
              <InfoCard
                icon={<SeatIcon />}
                title="Seats Taken"
                value={`${hive.seatTaken}  ${hive.seatTaken === 1 ? 'Seat' : 'Seats'}`}
              />
            </View>
            <View style={[tableLimitPx && { marginBottom: 10 }]}>
              <InfoCard
                icon={<SeatXIcon />}
                title="Seats Available"
                value={`${hive.numberOfSeats - hive.seatTaken}  ${
                  hive.numberOfSeats - hive.seatTaken === 1 ? 'Seat' : 'Seats'
                }`}
              />
            </View>
          </View>
          <View style={[!tableLimitPx && { flexDirection: 'row', marginTop: 20 }]}>
            <View style={[tableLimitPx && { marginBottom: 10 }]}>
              <InfoCard
                icon={<ClockIcon />}
                title="Total Hive Time"
                value={`${Math.round((hiveAnalytics?.totalTime || 0) / 60000)} Mins`}
              />
            </View>

            <InfoCard
              icon={<ControllerIcon />}
              title="Total Hive Games"
              value={`${hiveAnalytics?.numberOfGames || 0}`}
            />
          </View>
          {!tableLimitPx && (
            <View style={[styles.hexagonButtonsContainer]}>
              <View style={{ marginBottom: 8 }}>
                <HexagonIconButton
                  onPress={() => {
                    navigation.navigate('HiveReports', { ...route.params });
                  }}
                  text={'Reports'}
                  iconRight={<ChevronRight name="chevron-right" size={14} style={styles.arrow} />}
                  hexagonIcon={<HexagonFile />}
                  mainContainerStyle={{ width: 177 }}
                  textContainerStyle={{ width: 177, paddingLeft: 70 }}
                />
              </View>

              <HexagonIconButton
                onPress={() => {
                  navigation.navigate('HiveSettings', { ...route.params });
                }}
                text={'Settings'}
                iconRight={<ChevronRight name="chevron-right" size={14} style={styles.arrow} />}
                hexagonIcon={<HexagonSettings />}
                mainContainerStyle={{ width: 177 }}
                textContainerStyle={{ width: 177, paddingLeft: 70 }}
              />
            </View>
          )}

          {/* {!isMobile && hive.size !== HiveSize.SingleUser && (
            <View style={{ height: 150, justifyContent: 'center' }}>
              <LeaderBoardButton
                noMargin
                action={() => setShowLeaderboard(true)}
                loading={hiveUsersLoading}
              />
            </View>
          )} */}
        </View>
        <Divider empty height={40} />
        <View style={styles.container}>
          <View style={[styles.tableContainer]}>
            {!tableLimitPx && (
              <View style={styles.header}>
                {[
                  'Last Name',
                  'First Name',
                  'User Name',
                  'Password',
                  'Total Games',
                  'Total Time (mins)',
                  'Points',
                ]
                  .filter(column => !(column === 'Points' && dateRange?.startDate)) // hide Points if startDate exists
                  .map(column => (
                    <View style={styles.headerView} key={column}>
                      <Text style={styles.headerText}>{column}</Text>
                    </View>
                  ))}
                <View style={styles.headerViewLast} />
                <Text />
              </View>
            )}
            {users?.map((user, index: number) => {
              return !tableLimitPx ? (
                <UserItem
                  key={user.id}
                  user={user}
                  index={index}
                  editAction={() => handleEditPress(user)}
                  removeAction={() => handleRemovePress(user)}
                  rangeQuery={dateRange.startDate !== undefined}
                  navigation={navigation}
                  hiveId={id}
                />
              ) : (
                <UserItemSmall
                  key={user.id}
                  user={user}
                  index={index}
                  editAction={() => handleEditPress(user)}
                  removeAction={() => handleRemovePress(user)}
                  rangeQuery={dateRange.startDate !== undefined}
                  navigation={navigation}
                  hiveId={id}
                />
              );
            })}
          </View>
        </View>
        <Divider empty height={40} />
      </ComponentWrapper>
      <PaymentPopup
        visible={isPaymentPopupVisible}
        setVisible={setPaymentPopupClose}
        onPayClick={handlePay}
      />
      <EditPopup
        firstName={selectedUser?.firstName ?? ''}
        lastName={selectedUser?.lastName ?? ''}
        userName={selectedUser?.userName ?? ''}
        password={selectedUser?.password ?? ''}
        visible={isEditPopupVisible}
        setVisible={setEditPopupClose}
        loading={editUserFromHiveLoading}
        onSavePress={handleEditSubmitPress}
      />
      <RemovePopup
        firstName={selectedUser?.firstName ?? ''}
        lastName={selectedUser?.lastName ?? ''}
        visible={isRemovePopupVisible}
        setVisible={setRemovePopupClose}
        loading={removeUserFromHiveLoading}
        onRemovePress={handleRemoveSubmitPress}
      />
      <DateRangeModal
        visible={isCalendarVisible}
        setVisible={setCalendarVisible}
        dateRange={dateRange}
        onApply={(dateRange: DateRange) => {
          setDateRange(dateRange);
          refetchHiveUsers();
          refetchHiveAnalyticsData();
        }}
      />
      <ChangeHiveNamePopup
        currentName={hive.name}
        visible={isChangeNamePopupVisible}
        setVisible={setChangeNamePopupClose}
        loading={changeHiveNameLoading}
        onChangePress={handleChangeName}
      />
      <ErrorPopup
        isVisible={hasError.popup}
        setVisible={() => setHasError(v => ({ ...v, popup: false }))}
        text={hasError.text}
      />
      <OnBoardPopup visible={isOnBoardVisible} setVisible={setIsOnBoardVisible} />
    </>
  );
};

export { MyHiveData };
const styles = StyleSheet.create({
  topContainer: {
    zIndex: 100,
    height: 60,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    ...mbPlatformStyle({
      mobile: {
        paddingHorizontal: 20,
        marginTop: 0,
      },
      web: {
        paddingHorizontal: 60,
        marginTop: 60,
      },
    }),
  },
  smallUserInfoContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    maxWidth: 500,
    backgroundColor: 'transparent',
    marginTop: 10,
    paddingVertical: 10,
    borderRadius: 12,
  },
  onBoardText: {
    fontFamily: 'Secular One',
    fontSize: 15,
    fontWeight: '400',
    marginTop: 4,
    color: '#E89823',
  },
  mobileOnBoardText: {
    alignSelf: 'flex-end',
    marginRight: 5,
  },
  statusText: {
    fontFamily: 'Inter',
    fontSize: 15,
    fontWeight: '500',
  },
  title: {
    fontFamily: 'Secular One',
    fontSize: 28,
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 4 },
    textShadowRadius: 2,
    color: '#E87030',
    marginHorizontal: 25,
  },
  container: {
    width: '100%',
    ...mbPlatformStyle({
      mobile: {
        paddingHorizontal: 12,
      },
      web: {
        paddingHorizontal: 54,
      },
    }),
  },
  tableContainer: {
    width: '100%',
    borderRadius: 16,
    backgroundColor: '#FFF5DD',
    padding: 20,
    borderWidth: 2,
    borderColor: '#FFCF53',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    backgroundColor: '#FEE9B2',
    borderRadius: 12,
  },
  headerText: {
    fontFamily: 'Secular One',
    fontWeight: '700',
    fontSize: 15,
    alignSelf: 'flex-start',
    color: '#333333',
  },
  headerView: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerViewLast: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...mbPlatformStyle({
      web: {
        paddingHorizontal: 54,
      },
    }),
  },
  infoContainer: {
    width: '100%',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    flexWrap: 'wrap',
    ...mbPlatformStyle({
      mobile: {
        paddingHorizontal: 20,
        paddingRight: 10,
        justifyContent: 'center',
      },
      web: {
        paddingHorizontal: 54,
        paddingRight: 34,
      },
    }),
  },
  infoCard: {
    backgroundColor: '#FEDD88',
    borderRadius: 10,
    paddingTop: 17,
    paddingLeft: 16,
    paddingBottom: 23,
    ...mbPlatformStyle({
      mobile: {
        width: 164,
        marginRight: 10,
      },
      web: {
        width: 251,
        marginRight: 10,
      },
    }),
  },
  infoCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 17,
  },
  infoKey: {
    color: '#453B3B',
    fontSize: 13,
    marginLeft: 2,
  },
  infoValue: {
    fontFamily: 'Secular One',
    fontWeight: '400',
    color: '#333333',
    ...mbPlatformStyle({
      mobile: {
        fontSize: 18,
      },
      web: {
        fontSize: 21,
      },
    }),
  },
  copyButton: {
    marginLeft: 8,
    padding: 2,
  },
  hiveKeyChip: {
    flexDirection: 'row',
    width: undefined,
    paddingHorizontal: 14,
  },
  copiedIcon: {
    color: '#62B655',
    fontSize: 16,
    fontWeight: 'bold',
  },
  copiedLabel: {
    position: 'absolute',
    bottom: -22,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 12,
    fontFamily: 'Secular One',
    color: '#62B655',
    backgroundColor: '#F0FAF0',
    paddingVertical: 2,
    borderRadius: 10,
    overflow: 'hidden',
  },
  chips: {
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFEED6',
    marginHorizontal: 5,
    borderRadius: 20,
    ...mbPlatformStyle({
      mobile: {
        width: 160,
      },
      web: {
        width: 160,
      },
    }),
  },
  chipsText: {
    fontFamily: 'Inter',
    color: '#E89823',
    textAlign: 'center',
    fontSize: 15,
    fontWeight: '500',
  },
  bold: {
    fontWeight: '700',
  },
  editName: {
    marginLeft: 8,
    backgroundColor: '#FEDD88',
    borderRadius: 8,
    padding: 6,
  },
  arrow: {
    position: 'absolute',
    right: 10,
    padding: 0,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 4,
  },
  hexagonButtonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 20,
    marginLeft: 8,
  },
  date: {
    alignItems: 'center',
    borderWidth: 1,
    borderColor: mbApplyTransparency('#FFFFFF', 0.19),
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 10,
    backgroundColor: '#FFF5DD',
    width: 300,
    height: 40,
    fontFamily: 'Secular One',
  },
});
